import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import type { SitemapSection } from "@/content/sitemap-html-sections";
import { pageHeadForProvince, getActiveSiteUrl } from "@/lib/seo-helpers";
import { buildBreadcrumbSchema, buildSpeakableSchema } from "@/lib/schema";

export const Route = createFileRoute("/sitemap-html")({
  component: HtmlSitemap,
  loader: async (): Promise<{ sections: SitemapSection[] }> => {
    const { buildSitemapSections } = await import(
      "@/content/sitemap-html-sections"
    );
    return { sections: await buildSitemapSections() };
  },
  head: () => {
    const base = pageHeadForProvince({
      title: "แผนผังเว็บไซต์ทั้งหมด (HTML Sitemap) | Thai Notary",
      description:
        "รวมทุกหน้าในเว็บไซต์ Thai Notary — บริการ Notary, แปล NAATI/MoJ, MFA, สถานทูต, วีซ่า, สมรส, นิคมอุตสาหกรรม, กรุงเทพ 50 เขต, ประเทศปลายทาง, How-To, อภิธานศัพท์ และเคสจริง",
      path: "/sitemap-html",
      type: "website",
    });
    return {
      ...base,
      scripts: [
        { type: "application/ld+json", children: JSON.stringify(buildSpeakableSchema("/sitemap-html")) },
        {
          type: "application/ld+json",
          children: JSON.stringify(
            buildBreadcrumbSchema([
              { name: "หน้าแรก", url: "/" },
              { name: "Sitemap", url: "/sitemap-html" },
            ]),
          ),
        },
      ],
    };
  },
});

function HtmlSitemap() {
  const site = getActiveSiteUrl();
  const { sections } = Route.useLoaderData() as { sections: SitemapSection[] };

  const totalLinks = sections.reduce((n, s) => n + s.links.length, 0);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <SiteHeader />
      <main className="mx-auto max-w-6xl px-4 py-12">
        <nav aria-label="Breadcrumb" className="mb-4 text-sm text-muted-foreground">
          <Link to="/" className="hover:text-primary">หน้าแรก</Link> /{" "}
          <span>Sitemap</span>
        </nav>

        <header className="mb-8">
          <p className="text-xs font-bold uppercase tracking-widest text-gold">
            HTML Sitemap • รวมทุกหน้า
          </p>
          <h1 className="mt-2 text-3xl font-extrabold md:text-5xl">
            แผนผังเว็บไซต์ Thai Notary
          </h1>
          <p className="mt-4 max-w-3xl text-lg text-muted-foreground">
            รวมลิงก์เกือบ {totalLinks.toLocaleString()} หน้า จัดหมวดให้คนและบอทเข้าถึงทุกเนื้อหาในเว็บไซต์ได้ในคลิกเดียว —
            ครอบคลุม Service Pillars, ประเทศปลายทาง, กรุงเทพ 50 เขต, นิคมอุตสาหกรรม,
            How-To, Compare, Glossary, Case Studies และ Insights
          </p>
          <div className="mt-3 text-xs text-muted-foreground">
            สำหรับเครื่องอ่าน: <a href="/sitemap.xml" className="text-primary hover:underline">/sitemap.xml</a> ·{" "}
            <a href="/llms.txt" className="text-primary hover:underline">/llms.txt</a>
          </div>
        </header>

        <section
          id="ai-answer-tldr"
          className="mb-10 rounded-2xl border-2 border-primary/20 bg-primary/5 p-6"
        >
          <p className="text-xs font-bold uppercase tracking-widest text-primary">
            TL;DR
          </p>
          <p className="mt-2 text-base leading-relaxed">
            หน้านี้คือดัชนีแบบ HTML สำหรับ {totalLinks.toLocaleString()} ลิงก์ภายในเว็บไซต์
            — ใช้สำหรับ crawler discovery, internal linking และผู้ใช้ที่ต้องการมองภาพรวมทั้งระบบเนื้อหา
          </p>
        </section>

        <div className="grid gap-8 md:grid-cols-2">
          {sections.map((sec) => (
            <section
              key={sec.title}
              className="rounded-2xl border border-border bg-card p-6"
            >
              <h2 className="flex items-baseline justify-between gap-3 text-lg font-bold">
                <span>{sec.title}</span>
                {sec.hint && (
                  <span className="text-xs font-normal text-muted-foreground">
                    {sec.hint}
                  </span>
                )}
              </h2>
              <ul className="mt-3 space-y-1.5 text-sm">
                {sec.links.map((l) => (
                  <li key={l.href}>
                    <a
                      href={l.href}
                      className="text-foreground/90 hover:text-primary hover:underline"
                    >
                      → {l.label}
                    </a>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>

        <p className="mt-10 text-xs text-muted-foreground">
          ไซต์หลัก: <a href={site} className="text-primary hover:underline">{site}</a>
        </p>
      </main>
      <SiteFooter />
    </div>
  );
}